<template>
    <div class="modal-content">
        <div class="modal-header">
            <h5 class="modal-title" id="flyte-form-modal-label">
                {{ $root.text('Flyte file sender', null, true) }}
            </h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" :disabled="sending">
                <span aria-hidden="true">×</span>
            </button>
        </div>
        <div class="modal-body">
            <div v-if="sent">
                <div v-if="sent" class="alert alert-success" role="alert">
                    <div class="row">
                        <div class="col-1" style="max-width: 30px"><i class="far fa-check"></i></div>
                        <div class="col" v-html="sent"></div>
                    </div>
                </div>
                <router-link :to="{ name: 'my-account' }" data-dismiss="modal">
                    {{ $root.text(
                        'You can check what you have sent on your {my account} page.',
                        { 'my account': $root.text('my account', null, true) },
                        true
                    ) }}
                </router-link>
            </div>
            <div class="row" :style="'display: ' + (!sent ? 'flex' : 'none')">
                <div class="col-md-6 col-12" style="max-height: 439px; overflow-y: auto">
                    <vue-dropzone
                        v-if="dropzone_options"
                        ref="flyteDropzone"
                        id="flyte-dropzone"
                        :options="dropzone_options"
                        v-on:vdropzone-sending="onSendingFile"
                        v-on:vdropzone-upload-progress="onProgressUpdate"
                        v-on:vdropzone-success="onFileUploaded"
                        v-on:vdropzone-error="onUploadError"
                        v-on:vdropzone-drag-over="onDragOver"
                        v-on:vdropzone-drag-leave="onDragLeave"
                        v-on:vdropzone-drop="onDragLeave"
                        v-on:vdropzone-file-added="onFileAdded"
                        :useCustomSlot=true
                    >
                        <div v-for="file in uploaded_files" :key="file.id" class="dz-uploaded-file p-2 mb-1">
                            <div class="dz-thumbnail">
                                <span v-if="!file.preview_image">
                                    <img
                                        :src="$root.imagePlaceholder(file.filename)"
                                        class="img-fluid"
                                        alt=""
                                    >
                                </span>
                                <img
                                    v-else
                                    :src="file.preview_image"
                                    class="img-fluid"
                                    alt=""
                                    v-on:mouseover="viewImageIcon(file.id, true)"
                                    v-on:mouseout="viewImageIcon(file.id, false)"
                                >
                                <div
                                    v-if="file.preview_image"
                                    class="view-image-icon"
                                    :id="'view-image-icon' + file.id"
                                    v-b-tooltip.hover
                                    :title="$root.text('Enlarge', null, true)"
                                    @click="viewImage(file)"
                                    v-on:mouseover="viewImageIcon(file.id, true)"
                                    v-on:mouseout="viewImageIcon(file.id, false)"
                                >
                                    <i class="fal fa-expand-arrows"></i>
                                </div>
                            </div>
                            <div class="dz-filename">{{ file.filename }}</div>
                            <div class="dz-size" v-html="$root.formatFileSize(file.file_size)"></div>
                            <div class="dz-remove">
                                <a
                                    v-if="!sending"
                                    href="#"
                                    v-b-tooltip.hover
                                    :title="$root.text('Delete', null, true)"
                                    @click.prevent="removeUploadedFile(file.id)"
                                >
                                    <i class="fal fa-trash-alt"></i>
                                </a>
                                <span v-if="sending"><i class="fal fa-trash-alt"></i></span>
                            </div>
                            <div
                                class="dz-upload-info"
                                :id="'upload-info' + file.id"
                                style="left: 160px"
                            >
                                <i class="fal fa-clock mr-1"></i> {{ $root.text('Pending upload', null, true) }}
                            </div>
                            <div
                                class="dz-progress"
                                :id="'upload-progress' + file.id"
                                style="width: calc(100% - 172px)"
                            >
                                <div class="dz-upload"></div>
                            </div>
                        </div>
                        <a
                            href="#"
                            class="dz-add-files"
                            id="flyte-add-files"
                            @click.prevent="doNothing()"
                            :style="dzAddFilesStyle()"
                        >
                            <i class="far fa-cloud-upload-alt font-size-20 mr-1"></i>
                            {{ $root.text('Click or drop files here to upload', null, true) }}
                            <h5>{{ $root.text('(Maximum file size 10GB)') }}</h5>
                        </a>
                    </vue-dropzone>
                </div>
                <div class="col-md-6 col-12 mt-md-0 mt-3" style="padding-bottom: 140px">
                    <div v-if="errors" v-html="errors" class="alert alert-danger" role="alert"></div>

                    <form v-if="$root.hasPermission('flyte_access')" v-on:submit.prevent="onSubmit()">
                        <div class="form-group dropdown">
                            <label id="flyte-recipients-label" class="mandatory-label">
                                {{ $root.text('Recipient email(s)', null, true) }} *
                            </label>
                            <div
                                class="form-control mandatory-field pb-0"
                                id="flyte-recipients"
                                @click="focusRecipientKeyword()"
                                style="height: auto; min-height: 38px"
                            >
                                <span
                                    v-for="item in recipients"
                                    :key="item.email"
                                    class="badge badge-soft-secondary contact-links font-size-12 mr-2 mb-2"
                                    v-b-tooltip.hover
                                    title=""
                                >
                                    <span v-if="!item.name">{{ item.email }}</span>
                                    <span v-else v-b-tooltip.hover :title="item.email">{{ item.name }}</span>
                                    <a
                                        v-if="!sending"
                                        href="#"
                                        v-b-tooltip.hover
                                        :title="$root.text('Remove', null, true)"
                                        @click.prevent="removeRecipient(item)"
                                    >
                                        <i class="far fa-times"></i>
                                    </a>
                                    <span v-if="sending">
                                        <i class="far fa-times"></i>
                                    </span>
                                </span>

                                <div style="display: inline-block; position: relative">
                                    <input
                                        v-model="recipient_keyword"
                                        type="text"
                                        id="flyte-recipient-keyword"
                                        class="form-control px-1 mb-2"
                                        v-on:focus="searchRecipient(true)"
                                        v-on:blur="focusRecipientKeyword(false)"
                                        style="width: 280px; max-width: calc(100% - 40px)"
                                    >
                                    <button
                                        v-if="recipient_keyword"
                                        type="button"
                                        class="btn btn-secondary btn-sm"
                                        v-b-tooltip.hover
                                        :title="$root.text('Add recipient', null, true)"
                                        @click="addRecipient()"
                                        :disabled="sending"
                                        style="padding-top: 1px; padding-bottom: 1px; position: absolute; top: 0"
                                    >
                                        <i class="far fa-plus"></i>
                                    </button>
                                </div>
                            </div>

                            <div
                                class="dropdown-menu flyte-recipient-dropdown"
                                style="width: 280px; max-height: 230px; overflow-y: auto"
                            >
                                <div v-for="item in recipient_results" :key="item.id">
                                    <a
                                        class="dropdown-item"
                                        href="#"
                                        @click.prevent="addRecipient(item)"
                                    >
                                        {{ item.first_name }} {{ item.last_name }}
                                        <p class="small mb-1">{{ item.email }}</p>
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="flyte-message">{{ $root.text('Message', null, true) }}</label>
                            <textarea
                                v-model="message"
                                id="flyte-message"
                                class="form-control"
                                :readonly="sending"
                                style="height: 120px"
                            ></textarea>
                        </div>
                        <div>
                            <label for="flyte-link-expires-select">{{ $root.text('Expires', null, true) }}</label>
                            <searchable-option-list
                                :key="'flyte-link-expires-select-' + ($root.current_language || 0)"
                                v-model="link_expires_select"
                                :value="link_expires_select"
                                :options="getLinkExpiryPresets()"
                                :field_id="'flyte-link-expires-select'"
                                field_class="mandatory-field"
                                :readonly="sending"
                            ></searchable-option-list>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        <div
            v-if="$root.hasPermission('flyte_access')"
            class="modal-footer"
            :style="'position: relative; ' + modalFooterStyle()"
        >
            <div v-if="uploading" class="progress-display" v-html="uploadProgress()"></div>

            <span class="button-holder" :style="addMoreFilesStyle()">
                <button type="button" class="btn btn-secondary" id="flyte-add-more-files" :disabled="sending">
                    <i class="far fa-cloud-upload-alt mr-1"></i>
                    {{ $root.text('Add more files', null, true) }}
                </button>
            </span>

            <span v-if="!sending">
                <span>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="onSubmit()"
                        :disabled="(uploading || (uploaded_files.length == 0))"
                    >
                        <i class="far fa-paper-plane mr-1"></i> {{ sendButtonText() }}
                    </button>
                </span>
            </span>
            <span v-else>
                <button type="button" class="btn btn-primary">
                    <i class="fal fa-spinner-third fa-spin mr-1"></i>
                    <span style="opacity: 0.5">{{ sendButtonText() }}</span>
                </button>
            </span>

            <div
                v-if="dropzone_options && (upload_progress.bytes_total > (dropzone_options.maxFilesize / 10))"
                :style="'width: 100%; ' + addMoreFilesStyle()"
            >
                {{ $root.text(
                    '{remaining_size} remaining of max. {max_size}',
                    {
                        remaining_size: $root.formatFileSize(dropzone_options.maxFilesize - upload_progress.bytes_total),
                        max_size: '10GB'
                    }
                ) }}
            </div>
        </div>
    </div>
</template>

<script>
    import vue2Dropzone from 'vue2-dropzone';
    export default {
        components: {
            'vue-dropzone': vue2Dropzone
        },
        data () {
            return {
                errors: false,
                uploading: false,
                upload_progress_message: false,
                sending: false,
                sent: false,
                uploaded_files: [],
                upload_progress: {
                    progress: 0,
                    bytes_total: 0,
                    bytes_sent: 0
                },
                dropzone_options: null,
                recipients: [],
                message: null,
                link_expires: null,
                link_expires_select: '7d',
                recipient_keyword: '',
                block_recipient_keyword_auto_process: false,
                recipient_results: [],
                keypress_time: 0,
                updating_link_expiry_date: false
            }
        },
        mounted () {
            var vm = this;
            this.dropzone_options = {
                url: this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-v2',
                createImageThumbnails: true,
                maxThumbnailFilesize: 20,
                thumbnailWidth: 50,
                thumbnailHeight: 50,
                uploadMultiple: false,
                parallelUploads: 1,
                chunking: true,
                forceChunking: true,
                parallelChunkUploads: false,
                chunkSize: 10000000,
                retryChunks: true,
                retryChunksLimit: 5,
                maxFilesize: (10 * 1000 * 1000 * 1000),
                autoProcessQueue: true,
                timeout: 0,
                clickable: '#flyte-add-files, #flyte-add-more-files',
                headers: {
                    'Authorization': 'Bearer ' + this.$root.token
                },
                accept: function (file, done) {
                    if (
                        !file.type
                        && (vm.$root.client.upload_disallowed_file_types.indexOf(file.type.toLowerCase()) > -1)
                    ) {
                        done(
                            {
                                error: vm.$root.text('File type not accepted', null, true),
                                filename: file.name,
                                file_type: file.type
                            }
                        );

                        return null;
                    }
                    const ext = vm.$root.getFileExtension(file.name);
                    if (ext && (vm.$root.client.upload_disallowed_file_extensions.indexOf(ext) > -1)) {
                        done(
                            {
                                error: vm.$root.text('File type not accepted', null, true),
                                filename: file.name,
                                file_type: file.type
                            }
                        );

                        return null;
                    }
                    done();
                }
            };
        },
        watch: {
            recipient_keyword: function (val) {
                this.$root.unHighlightErrors('flyte-recipients');
                if (val) {
                    this.searchRecipient(true);

                    return null;
                }
                $('.flyte-recipient-dropdown').dropdown('hide');
            },
            sent: function (val) {
                if (val) {
                    $('#flyte-form-modal > div').removeClass('modal-xl');

                    return null;
                }
                $('#flyte-form-modal > div').addClass('modal-xl');
            },
            link_expires_select: function (val) {
                if (val === null) {
                    var vm = this;
                    setTimeout(function () {
                        vm.link_expires_select = '7d';
                    }, 200);

                    return null;
                }
                this.setDate('link_expires', val);
            }
        },
        methods: {
            resetForm: function () {
                if (!this.$root.hasPermission('flyte_access')) {
                    this.errors = this.$root.text('Sorry, you do not have access to this function.', null, true);

                    return false;
                }
                this.$parent.message = {
                    type: null,
                    title: '',
                    text: ''
                };
                this.errors = false;
                this.uploading = false;
                this.upload_progress_message = false;
                this.sending = false;
                this.sent = false;
                this.uploaded_files = [];
                this.upload_progress = {
                    progress: 0,
                    bytes_total: 0,
                    bytes_sent: 0
                };
                this.recipients = [];
                this.message = null;
                this.link_expires = null;
                this.link_expires_select = '7d';
                this.recipient_keyword = '';
                this.block_recipient_keyword_auto_process = false;
                if (this.$refs.flyteDropzone) {
                    this.$refs.flyteDropzone.removeAllFiles();
                }
                this.setDate('link_expires', '7d');
                $('.mandatory-label').removeClass('text-danger');
                $('.mandatory-field').removeClass('field-error');
            },
            onSubmit: function () {
                if (this.uploaded_files.length == 0) {
                    this.errors = this.$root.text('Please add at least 1 file', null, true);

                    return null;
                }

                if (this.uploading) {
                    this.showPleaseWaitMessage();

                    return null;
                }

                this.errors = false;
                this.sending = true;

                let files = [];
                this.uploaded_files.forEach((item) => {
                    files.push({
                        filename: item.filename,
                        file_type: item.file_type,
                        file_size: item.file_size,
                        temp_file: item.temp_file
                    });
                });

                let data = {
                    app_info: {
                        app_url: this.$root.app.app_url
                    },
                    type: 'email',
                    subject: 'default',
                    recipients: this.recipients,
                    flyte_files: files
                };
                if (this.recipient_keyword) {
                    data.recipients.push({email: this.recipient_keyword});
                }
                if (this.message) {
                    data.message = this.message;
                }
                if (this.link_expires) {
                    data.link_expires = this.link_expires;
                }

                /**
                 * Send share request to API
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/share';
                var vm = this;

                axios({
                    method: 'post',
                    url: url,
                    data: data,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    vm.sent = vm.$root.text(
                        'Your files have been sent.',
                        null,
                        true
                    );
                    switch (vm.link_expires_select) {
                        case '1d':
                            vm.sent += ' ' + vm.$root.text(
                                'They will be deleted automatically from {system_name} in 1 day.',
                                { system_name: vm.$root.client.client_name },
                                true
                            );
                            break;
                        case '7d':
                            vm.sent += ' ' + vm.$root.text(
                                'They will be deleted automatically from {system_name} in 1 week.',
                                { system_name: vm.$root.client.client_name },
                                true
                            );
                            break;
                        case '14d':
                            vm.sent += ' ' + vm.$root.text(
                                'They will be deleted automatically from {system_name} in 2 weeks.',
                                { system_name: vm.$root.client.client_name },
                                true
                            );
                            break;
                        case '1m':
                            vm.sent += ' ' + vm.$root.text(
                                'They will be deleted automatically from {system_name} in 1 month.',
                                { system_name: vm.$root.client.client_name },
                                true
                            );
                            break;
                    }
                })
                .catch(function (error) {
                    if (error.response.status == 401) {
                        // missing/lost API token - logout user
                        $('#flyte-form-modal').modal('hide');
                        vm.$root.doLogout();
                    }
                    // show error
                    vm.errors = vm.$root.text('Something went wrong, please try again', null, true);
                    if (error.response.data.error) {
                        if (typeof error.response.data.error === 'string') {
                            vm.errors = error.response.data.error;
                        } else if (typeof error.response.data.error === 'object') {
                            vm.errors = '';
                            for (const [key, value] of Object.entries(error.response.data.error)) {
                                vm.errors += value.join('<br>') + '<br>';
                                $('#flyte-' + key + '-label').addClass('text-danger');
                                $('#flyte-' + key).addClass('field-error');
                            }
                        }
                    }
                })
                .finally(() =>
                    vm.sending = false
                );
            },
            showPleaseWaitMessage: function () {
                this.$parent.message = {
                    type: null,
                    title: this.$root.text('Flyte file sender', null, true),
                    loading: true,
                    text: '<div style="text-align: center">'
                        + this.$root.text('Please wait until all files are uploaded', null, true) + '</div>'
                };
                $('#message-modal').modal('show');
                $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });
            },
            filesUploaded: function () {
                let count = 0;
                this.uploaded_files.forEach((file, index) => {
                    if (file.temp_file) {
                        count++;
                    }
                });

                return count;
            },
            doNothing: function () {
                return false;
            },
            onSendingFile: function (file, xhr, formData) {
                formData.append('id', this.$root.getUploadFileId(file));
            },
            onProgressUpdate: function (file, progress, bytesSent) {
                if ((this.uploaded_files.length == 0) || (progress > 99.9)) {
                    return null;
                }
                let bytesTotal = 0;
                let bytesSentTotal = 0;
                this.uploaded_files.forEach((item) => {
                    bytesTotal += item.file_size;
                    if (item.id == file.upload.uuid) {
                        if (isNaN(bytesSent)) {
                            bytesSent = file.size * (progress / 100);
                        }
                        item.upload.progress = progress;
                        item.upload.bytes_sent = bytesSent;
                        bytesSentTotal += bytesSent;
                        $('#upload-info' + item.id).html(
                            this.$root.text('Uploading', null, true) + ' ' + this.$root.formatFileSize(bytesSent)
                                + ' ' + progress.toFixed(1) + '%'
                        );
                        $('#upload-progress' + item.id + ' > div').css('width', progress + '%');
                    } else if (item.upload.progress == 100) {
                        bytesSentTotal += item.file_size;
                    }
                });
                this.upload_progress.progress = ((bytesTotal * bytesSentTotal) > 0)
                    ? ((bytesSentTotal / bytesTotal) * 100) : 0;
                this.upload_progress.bytes_total = bytesTotal;
                this.upload_progress.bytes_sent = bytesSentTotal;
            },
            onFileUploaded: function (file, response) {
                if (this.uploaded_files.length == 0) {
                    return null;
                }
                // Find file.
                let itemIndex = -1;
                this.uploaded_files.forEach((item, index) => {
                    if (item.id == file.upload.uuid) {
                        itemIndex = index;
                    }
                });

                if (itemIndex > -1) {
                    this.uploaded_files[itemIndex].upload.progress = 100;
                    this.uploaded_files[itemIndex].upload.bytes_sent = file.size;
                    $('#upload-progress' + file.upload.uuid + ' > div').css('width', '100%');
                    if (!this.uploaded_files[itemIndex].preview_image) {
                        this.uploaded_files[itemIndex].preview_image = (file.dataURL || null);
                    }
                    $('#upload-info' + file.upload.uuid).html(
                        this.$root.text(
                            this.uploaded_files[itemIndex].preview_image ? 'Finalising upload...'
                                : 'Previewing...', null, true
                        )
                    );
                    this.getUploadInfo(this.uploaded_files[itemIndex].upload_id);
                    this.$refs.flyteDropzone.removeFile(file);
                }
                // If there are more files process queue??

            },
            getUploadInfo: function (fileId) {
                /**
                 * Send request to API.
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-info/' + fileId
                    + '/preview/500x500';
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    // Find file.
                    let itemIndex = -1;
                    vm.uploaded_files.forEach((item, index) => {
                        if (item.upload_id == response.data.file_id) {
                            itemIndex = index;
                        }
                    });
                    if (itemIndex < 0) {
                        return null;
                    }

                    // Check size.
                    if (response.data.file_size < vm.uploaded_files[itemIndex].file_size) {
                        vm.$parent.message = {
                            type: 'error',
                            title: vm.$root.text('Upload failed', null, true),
                            text: vm.$root.text(
                                'Sorry it looks like the file {filename} did not upload correctly. Please try again.',
                                {
                                    filename: '<strong style="overflow-wrap: anywhere">'
                                        + vm.uploaded_files[itemIndex].filename + '</strong>'
                                },
                                true
                            )
                        };
                        $('#message-modal').modal('show');
                        $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });
                        vm.removeUploadedFile(vm.uploaded_files[itemIndex].id, true);

                        return null;
                    }

                    vm.uploaded_files[itemIndex].temp_file = response.data.temp_file;
                    vm.onUploadComplete();
                    $('#upload-info' + vm.uploaded_files[itemIndex].id).html('');

                    if (response.data.preview) {
                        vm.uploaded_files[itemIndex].preview_image = response.data.preview.src;
                    }
                })
                .catch(function (error) {
                    // Do anything?
                });
            },
            onUploadComplete: function () {
                let allUploaded = true;
                this.uploaded_files.forEach((item) => {
                    if (!item.temp_file) {
                        allUploaded = false;
                    }
                });
                if (!allUploaded) {
                    return null;
                }
                this.uploading = false;
                this.upload_progress.progress = 0;
                this.$parent.message = {
                    type: null,
                    title: '',
                    text: ''
                };
                if ($('#message-modal').hasClass('show')) {
                    $('#message-modal').modal('hide');
                    this.$parent.resetModalBackdrop();
                }
            },
            onUploadError: function (file, message, xhr) {
                if (file.status != 'canceled') {
                    if (message.error) {
                        this.errors = message.error + ' ' + message.filename + ' (' + message.file_type + ')';
                    } else {
                        this.errors = this.$root.text('Something went wrong, please try again', null, true);
                    }
                }
                this.removeUploadedFile(file.upload.uuid, true);
                this.onUploadComplete();
                console.log(file, message);
            },
            onDragOver: function (event) {
                $('#flyte-dropzone').addClass('dz-drag-highlight');
            },
            onDragLeave: function (event) {
                $('#flyte-dropzone').removeClass('dz-drag-highlight');
            },
            onFileAdded: function (file) {
                if (this.sending) {
                    this.$refs.flyteDropzone.removeFile(file);

                    return null;
                }

                // Check file size.
                const appComponent = this.$root.findComponent(this.$root, 'main-app');
                if (file.size == 0) {
                    this.$refs.flyteDropzone.removeFile(file);
                    if (
                        (appComponent.message.title == this.$root.text('Damaged file detected', null, true))
                        || (appComponent.message.title == this.$root.text('Maximum upload size exceeded', null, true))
                    ) {
                        appComponent.message.text += '<br><br>'
                            + this.$root.text('{filename} appears to be damaged and has not been uploaded.',
                            { filename: '<strong style="overflow-wrap: anywhere">' + file.name + '</strong>' });
                        $('#message-modal').modal('show');
                        $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });

                        return null;
                    }
                    appComponent.message = {
                        type: 'error',
                        title: this.$root.text('Damaged file detected', null, true),
                        text: this.$root.text('{filename} appears to be damaged and has not been uploaded.',
                            { filename: '<strong style="overflow-wrap: anywhere">' + file.name + '</strong>' })
                    };
                    $('#message-modal').modal('show');
                    $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });

                    return null;
                }
                if ((this.upload_progress.bytes_total + file.size) > this.dropzone_options.maxFilesize) {
                    this.$refs.flyteDropzone.removeFile(file);
                    if (
                        (appComponent.message.title == this.$root.text('Damaged file detected', null, true))
                        || (appComponent.message.title == this.$root.text('Maximum upload size exceeded', null, true))
                    ) {
                        appComponent.message.text += '<br><br>'
                            + this.$root.text('The maximum size of files you can upload is {size}.',
                            { size: this.$root.formatFileSize(this.dropzone_options.maxFilesize) }, true) + '<br>'
                            + this.$root.text('{filename} has not been uploaded.',
                            { filename: '<strong style="overflow-wrap: anywhere">' + file.name + '</strong>' }, true);
                        $('#message-modal').modal('show');
                        $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });

                        return null;
                    }
                    appComponent.message = {
                        type: 'error',
                        title: this.$root.text('Maximum upload size exceeded', null, true),
                        text: this.$root.text('The maximum size of files you can upload is {size}.',
                            { size: this.$root.formatFileSize(this.dropzone_options.maxFilesize) }, true) + '<br>'
                            + this.$root.text('{filename} has not been uploaded.',
                            { filename: '<strong style="overflow-wrap: anywhere">' + file.name + '</strong>' }, true)
                    };
                    $('#message-modal').modal('show');
                    $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });

                    return null;
                }

                this.upload_progress.bytes_total += file.size;
                let newFile = {
                    id: file.upload.uuid,
                    upload_id: this.$root.getUploadFileId(file),
                    filename: file.name,
                    file_type: file.type,
                    file_size: file.size,
                    upload: {
                        progress: 0,
                        bytes_sent: 0
                    },
                    preview_image: (file.dataURL || null)
                };
                this.uploaded_files.push(newFile);
                this.uploading = true;
                this.errors = false;
                var vm = this;
                setTimeout(function () {
                    $('#upload-info' + file.upload.uuid).html(vm.$root.text('Preparing upload...', null, true));
                }, 500);
            },
            dzAddFilesStyle: function () {
                if (this.uploaded_files.length == 0) {
                    return 'display: block';
                }

                return 'display: none';
            },
            addMoreFilesStyle: function () {
                if (
                    !this.uploading
                    && !this.sending
                    && (this.uploaded_files.length > 0)
                    && (this.upload_progress.bytes_total < this.dropzone_options.maxFilesize)
                ) {
                    return 'display: block';
                }

                return 'display: none';
            },
            modalFooterStyle: function () {
                if (!this.sent) {
                    return 'display: flex';
                }

                return 'display: none';
            },
            uploadProgress: function () {
                if (this.upload_progress_message) {
                    return this.upload_progress_message;
                }
                const percent = this.upload_progress.progress.toFixed(1);

                return 'Uploading ' + ((percent < 100) ? percent : '100') + '%';
            },
            viewImage: function (file) {
                this.viewImageIcon(file.id, false);

                const appComponent = this.$root.findComponent(this.$root, 'main-app');
                appComponent.message = {
                    type: null,
                    title: file.filename,
                    text: '<div class="text-center"><img src="' + file.preview_image
                        + '" alt="" class="img-fluid" style="max-height: 500px"></div>'
                };
                $('#message-modal > div').addClass('modal-lg');
                $('#message-modal').modal('show');
                $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });
            },
            viewImageIcon: function (fileId, show) {
                if (show) {
                    $('#view-image-icon' + fileId).show();
                } else {
                    $('#view-image-icon' + fileId).hide();
                }
            },
            removeUploadedFile: function(fileId, confirmed = false) {
                let fileIndex = -1;
                this.uploaded_files.forEach((item, index) => {
                    if (item.id == fileId) {
                        fileIndex = index;
                    }
                });
                if (fileIndex > -1) {
                    if (confirmed) {
                        this.upload_progress.bytes_total -= this.uploaded_files[fileIndex].file_size;
                        const removeUploadFileId = (this.uploaded_files[fileIndex].upload.progress < 100) ? fileId
                            : null;

                        this.uploaded_files.splice(fileIndex, 1);

                        if (removeUploadFileId) {
                            this.$refs.flyteDropzone.getUploadingFiles().forEach((file) => {
                                if (file.upload.uuid == removeUploadFileId) {
                                    this.$refs.flyteDropzone.removeFile(file);
                                }
                            });
                            this.$refs.flyteDropzone.getQueuedFiles().forEach((file) => {
                                if (file.upload.uuid == removeUploadFileId) {
                                    this.$refs.flyteDropzone.removeFile(file);
                                }
                            });
                        }
                    
                        return null;
                    }

                    this.$parent.confirm_delete = {
                        title: this.$root.text('Delete file', null, true),
                        text: this.$root.text(
                            'Are you sure you want to delete: {filename}?',
                            { filename: this.uploaded_files[fileIndex].filename },
                            true
                        ),
                        component: this,
                        action: 'delete-file-' + fileId
                    };
                    $('#confirm-delete-modal').modal('show');
                    $('.modal-backdrop').css({ 'z-index': '1060', 'opacity': '0.25' });
                }
            },
            confirmDelete: function (action) {
                if (action.indexOf('delete-file-') > -1) {
                    const fileId = action.substr(12);
                    this.removeUploadedFile(fileId, true);
                }
            },
            focusRecipientKeyword: function (focus = true, delayed = false) {
                if (focus) {
                    $('#flyte-recipient-keyword').css('display', 'inline');
                    $('#flyte-recipient-keyword').focus();
                    var vm = this;
                    setTimeout(function () {
                        vm.block_recipient_keyword_auto_process = false;
                    }, 500);

                    return null;
                }

                if (!delayed) {
                    var vm = this;
                    setTimeout(function () {
                        vm.focusRecipientKeyword(false, true);
                    }, 500);

                    return null;
                }
                if (this.block_recipient_keyword_auto_process) {
                    return null;
                }
                if (this.recipient_keyword) {
                    this.addRecipient(null, false);
                }
                $('#flyte-recipient-keyword').css('display', 'none');
                $('.flyte-recipient-dropdown').dropdown('hide');
            },
            addRecipient: function (user = null, addNew = true) {
                if (!user && !this.recipient_keyword) {
                    return null;
                }
                const email = user ? user.email.trim() : this.recipient_keyword.trim();

                if ((email.length > 0) && !this.isRecipientAdded(email)) {
                    const recipient = {
                        email: email,
                        name: user ? user.first_name + ' ' + user.last_name : null,
                        user_id : user ? user.id : null
                    };
                    this.recipients.push(recipient);
                }

                this.recipient_keyword = '';
                this.recipient_results = [];
                setTimeout(function () {
                    $('.flyte-recipient-dropdown').dropdown('hide');
                }, 250);

                if (addNew) {
                    this.block_recipient_keyword_auto_process = true;
                    var vm = this;
                    setTimeout(function () {
                        vm.focusRecipientKeyword();
                    }, 200);
                }
            },
            removeRecipient: function (recipient) {
                this.$root.unHighlightErrors('flyte-recipients');
                this.block_recipient_keyword_auto_process = true;
                let itemIndex = -1;
                this.recipients.forEach((item, index) => {
                    if (item == recipient) {
                        itemIndex = index;
                    }
                });
                if (itemIndex > -1) {
                    this.recipients.splice(itemIndex, 1);
                }
            },
            searchRecipient: function (delay = false) {
                if (!this.recipient_keyword) {
                    return null;
                }
                if (delay) {
                    this.keypress_time = new Date();
                    var vm = this;
                    setTimeout(function () {
                        vm.searchRecipient();
                    }, 500);
                }

                const time = new Date();
                if ((time - this.keypress_time) < 500) {
                    return null;
                }

                /**
                 * Send request to API
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id
                    + '/users/!export|!pagination|approved|!declined|!deleted|!invited|kw::'
                    + this.$root.base64Encode(this.recipient_keyword);
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    vm.recipient_results = [];
                    response.data.users.forEach((item) => {
                        if ((item.email != vm.$root.user.email) && !vm.isRecipientAdded(item.email)) {
                            vm.recipient_results.push(item);
                        }
                    });
                    if (vm.recipient_results.length > 0) {
                        setTimeout(function () {
                            $('.flyte-recipient-dropdown').dropdown('show');
                        }, 200);

                        return null;
                    }
                    $('.flyte-recipient-dropdown').dropdown('hide');
                })
                .catch(function (error) {
                    if (error.response.status == 401) {
                        // missing/lost API token - logout user
                        $('#asset-share-modal').modal('hide');
                        vm.$root.doLogout();
                    }
                });
            },
            isRecipientAdded: function (email) {
                if (!email || (this.recipients.length == 0)) {
                    return false;
                }
                let found = false;
                this.recipients.forEach((item) => {
                    if (item.email.toLowerCase() == email.toLowerCase()) {
                        found = true;
                    }
                });

                return found;
            },
            setDate: function (dateField, value) {
                this.updating_link_expiry_date = true;
                if (value == 'never') {
                    this[dateField] = null;
                } else {
                    const newDate = new Date();
                    if (value == '1d') {
                        newDate.setDate(newDate.getDate() + 1);
                    }
                    if (value == '7d') {
                        newDate.setDate(newDate.getDate() + 7);
                    }
                    if (value == '14d') {
                        newDate.setDate(newDate.getDate() + 14);
                    }
                    if (value == '1m') {
                        newDate.setMonth(newDate.getMonth() + 1);
                    }
                    const month = ('0' + (newDate.getMonth() + 1)).slice(-2);
                    const day = ('0' + newDate.getDate()).slice(-2);
                    this[dateField] = newDate.getFullYear() + '-' + month + '-' + day;
                }
                this.updating_link_expiry_date = false;
            },
            sendButtonText: function () {
                if (this.uploaded_files.length == 0) {
                    return this.$root.text('Send', null, true);
                }
                if (this.uploaded_files.length == 1) {
                    return this.$root.text('Send 1 file', null, true) + ' '
                        + this.$root.text('total', null, true) + ' '
                        + this.$root.formatFileSize(this.upload_progress.bytes_total);
                }

                return this.$root.text('Send {x} files', { x: this.uploaded_files.length }, true) + ' '
                        + this.$root.text('total', null, true) + ' '
                        + this.$root.formatFileSize(this.upload_progress.bytes_total);
            },
            getLinkExpiryPresets: function () {
                const presets = [
                    {
                        id: '1d',
                        name: this.$root.text('In 1 day', null, true)
                    },
                    {
                        id: '7d',
                        name: this.$root.text('In 1 week', null, true)
                    },
                    {
                        id: '14d',
                        name: this.$root.text('In 2 weeks', null, true)
                    },
                    {
                        id: '1m',
                        name: this.$root.text('In 1 month', null, true)
                    }
                ];

                return presets;
            }
        }
    }
</script>
<style scoped>
    #flyte-dropzone {
        min-height: 300px;
    }

    #flyte-add-files {
        padding-top: 130px;
    }

    .dz-uploaded-file {
        cursor: default;
    }

    #flyte-add-more-files {
        position: absolute;
        left: 15px;
        top: 16px;
    }

    .progress-display {
        position: absolute;
        left: 15px;
        bottom: 10px;
        width: calc(100% - 100px);
    }

    #flyte-recipient-keyword {
        display: none;
        width: auto;
        height: 20px;
        border: none;
    }

    #flyte-recipient-keyword:focus {
        background-color: #f8f9fa;
    }
</style>
